import {Alert, Button, Form, Input, Select, Switch} from "antd";
import {useEffect, useRef, useState} from "react";
import {t} from "i18next";
import RoleSelectBox from "../role/RoleSelectBox";
import PermissionGroup from "../role/PermissionGroup";
import ApiService from "../../core/service/api-service";
import {Pages} from "../../AppRouter";
import {useHistory} from "react-router-dom";
import { Tabs } from 'antd';
import Flex from "../../core/components/Flex";
import FileUtils from "../../core/utils/file-utils";
import InstitutionSelect from "./InstitutionSelect";
import PhoneInput from "../../core/components/PhoneInput";
import PhoneUtil from "../../core/utils/phone-util";
import {PERMISSIONS, useUser} from "../../core/context/UserContext";

const { TabPane } = Tabs;

const PASSWORD_CHARSETS = ['ABCDEFGHJKLMNPQRSTUVWXYZ', 'abcdefghijkmnpqrstuvwxyz', '23456789'];

// 8 karakter, sembolsüz; büyük/küçük harf ve rakamdan en az birer tane. Karışan karakterler
// (0/O, 1/l/I) dışarıda — parola çoğu zaman sözlü/yazılı iletiliyor. getRandomValues düz HTTP'de de var.
function generatePassword(length = 8)
{
    const random = (max) => crypto.getRandomValues(new Uint32Array(1))[0] % max;
    const all = PASSWORD_CHARSETS.join('');
    const chars = PASSWORD_CHARSETS.map(set => set[random(set.length)]);
    while (chars.length < length)
        chars.push(all[random(all.length)]);

    for (let i = chars.length - 1; i > 0; i--)
    {
        const j = random(i + 1);
        [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    return chars.join('');
}

export default function UserCreateEdit({userId, onSaved})
{
    const history = useHistory();
    const formRef = useRef();
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [disabledPermissions, setDisabledPermissions] = useState([])

    const [user, setUser] = useState({permissionIdList: []});

    const [signImage, setSignImage] = useState('');

    // 2FA yalnız kanalı olan kullanıcıda açılabilir: ne telefonu ne e-postası olan kullanıcı 2FA
    // açıkken kod alamaz ve HİÇ giriş yapamaz (sunucu girişi reddediyor). Anahtar buna göre pasifleşir.
    const [contact, setContact] = useState({ email: '', phone: '' });

    // Sistem genelindeki 2FA anahtarı (appsettings App.MfaEnabled). Kapalıysa kullanıcının kendi
    // bayrağı girişi ETKİLEMEZ; anahtarı açık göstermek yanıltıcı olduğu için kapalı ve pasif çizilir.
    const {user: currentUser, isSuperAdmin} = useUser();
    const systemMfaEnabled = currentUser?.config?.mfaEnabled !== false;

    // Süper yönetici hesabı alt seviyeden düzenlenemez. Sadece izin vermeyi kapatmak yetmezdi:
    // parolasını değiştirip hesabına girmek de bir yetki yükseltmesidir. Sunucu da reddediyor.
    const [locked, setLocked] = useState(false);

    // Düzenlenen kullanıcıda hâlihazırda duran izinler (rol + kişisel). Bunlar korunabildiği için
    // rol seçiminde "veremeyeceğim izin" hesabından muaftır — yeni kullanıcıda liste boştur.
    const [targetPermissions, setTargetPermissions] = useState([]);

    // Telefon artık zorunlu değil: boş bırakılınca "90-" gider; sunucu bunu "telefon yok" sayar.
    const toPhoneValue = (phone) =>
        `${phone?.code || 90}-${PhoneUtil.clearPhoneFormat(phone?.phone || '')}`

    const save = async (form) => {
        if(userId)
        {
            const response = await ApiService.UserManager.EditUser(userId, {
                ...form,
                permissionIdList: selectedPermissions,
                doctorSignPicture: signImage.data,
                phone: toPhoneValue(form.phone),
            })

            if(response.success && onSaved)
                onSaved()
        }
        else
        {
            const response = await ApiService.UserManager.CreateUser({
                ...form,
                permissionIdList: selectedPermissions,
                doctorSignPicture: signImage.data,
                phone: toPhoneValue(form.phone),
            })

            if(response.success)
            {
                if(onSaved)
                    onSaved()
                else
                    history.push(Pages.User.Edit(response.data))
            }
        }
    }

    const getUser = async () => {
        if(userId)
        {
            const response = await ApiService.UserManager.GetUser(userId);
            if(response.success)
            {
                const currentPermissions = [
                    ...(response.data.permissionIdList || []),
                    ...(response.data.role?.permissionIdList || []),
                ]

                // Kilitliyse form hiç çizilmez; alanları doldurmaya çalışmadan çıkıyoruz (form
                // unmount olduğu için formRef de boşalır).
                if(!isSuperAdmin() && currentPermissions.includes(PERMISSIONS.SUPER_ADMIN))
                {
                    setLocked(true)
                    return
                }

                setTargetPermissions(currentPermissions)
                setSelectedPermissions(response.data.permissionIdList)
                setDisabledPermissions(response.data.role?.permissionIdList)
                // Telefon "90-5321234567" olarak gelir; numarası boş kayıtta ("90-") kanal YOKTUR.
                // Ham alana bakmak "kanalı var" sanmaya yol açardı — numara kısmını ayıklıyoruz.
                setContact({
                    email: response.data.email,
                    phone: response.data.phone?.split('-')[1] || '',
                })

                formRef.current.setFieldsValue({
                    ...response.data,
                    roleId: response.data.role.id,
                    ...response.data.userInformation,
                    phone: {
                        code: parseInt(response.data.phone?.split('-')[0]),
                        phone: PhoneUtil.phoneFormat(response.data.phone?.split('-')[1])
                    },
                })

                const image = await ApiService.UserManager.GetDoctorSignImage(userId);

                setSignImage({
                    data: null,
                    url: image?.data
                })
            }
        }
    }

    const selectImage = async () => {
        const file = await FileUtils.getFileAsBase64({accept: ''});
        setSignImage(file)
    }

    const onValuesChange = (field, formData) => {
        setContact({ email: formData.email, phone: formData.phone?.phone })

        formRef.current.setFieldsValue({
            ...formData,
            phone: {
                code: formData.phone.code || 90,
                phone: PhoneUtil.phoneFormat(formData.phone.phone)
            },
            // Son kanal da silinirse 2FA kendiliğinden kapanır — kaydetmede hata almak yerine tutarlı kalırız
            mfaEnabled: (formData.email || formData.phone?.phone) ? formData.mfaEnabled : false,
        })
    }

    useEffect(() => {
        getUser()
    }, [])

    if (locked)
    {
        return (
            <Alert
                type={'warning'}
                showIcon
                message={t('You are not allowed to edit this user')}
            />
        )
    }

    return (
        <>
                <Form
                    size={'small'}
                    layout={'vertical'}
                    ref={formRef}
                    onFinish={save}
                    onValuesChange={onValuesChange}
                    initialValues={{
                        phone: {
                            code: 90
                        },
                        enabled: true,
                        mfaEnabled: true,
                        studyOpenLockEnabled: false,
                    }}
                >

                    <Form.Item
                        label={t('Username')}
                        name={'username'}
                        // Düzenlemede alan kilitli; adında boşluk olan eski kullanıcı kaydedilemez hale gelmesin
                        rules={userId ? [] : [
                            {required: true},
                            {pattern: /^\S+$/, message: t('Username cannot contain spaces')}
                        ]}
                    >
                        <Input disabled={userId} />
                    </Form.Item>

                    <Form.Item
                        label={t('Active/Inactive')}
                        name={'enabled'}
                    >
                        <Select
                            size={'small'}
                        >
                            <Select.Option value={true}>{t('Active')}</Select.Option>
                            <Select.Option value={false}>{t('Inactive')}</Select.Option>
                        </Select>
                    </Form.Item>

                    {/* İki aşamalı doğrulama — TEK anahtar. Kodu SMS ile mi e-posta ile mi alacağını
                        kullanıcı giriş anında seçer; hangi iletişim bilgisi doluysa o seçenek çıkar.
                        Kanalı olmayan kullanıcıda açılamaz (kilitlenme koruması). */}
                    {
                        systemMfaEnabled ? (
                            <Form.Item
                                label={t('Two-factor authentication')}
                                name={'mfaEnabled'}
                                valuePropName={'checked'}
                                extra={
                                    !contact.email && !contact.phone
                                        ? t('Requires a phone number or an e-mail address')
                                        : t('At login the user receives a verification code by SMS or e-mail.')
                                }
                            >
                                <Switch disabled={!contact.email && !contact.phone} />
                            </Form.Item>
                        ) : (
                            <>
                                {/* Sistem genelinde kapalıyken anahtar KAPALI ve pasif çizilir (giriş akışına
                                    etkisi yok), ama kullanıcının kayıtlı bayrağına dokunulmaz: gizli alanda
                                    korunur ki 2FA yeniden açıldığında kimse sessizce kapsam dışı kalmasın. */}
                                <Form.Item
                                    name={'mfaEnabled'}
                                    valuePropName={'checked'}
                                    hidden
                                >
                                    <Switch/>
                                </Form.Item>
                                <Form.Item
                                    label={t('Two-factor authentication')}
                                    extra={t('Two-factor authentication is disabled system-wide')}
                                >
                                    <Switch checked={false} disabled/>
                                </Form.Item>
                            </>
                        )
                    }


                    {/* Görüntü açma + rapor yazma kilidi — kullanıcı bazlı, varsayılan kapalı. Açıksa kilidi açık
                        hastanelerde açtığı okunmamış çekim ona atanır, başka doktorun süren kilidindeki çekimleri
                        açamaz; rapor ekranını açınca da raportör olarak atanır. Kapalıysa (admin vb.) kilit almaz. */}
                    <Form.Item
                        label={t('Study/Report Open Lock')}
                        name={'studyOpenLockEnabled'}
                        valuePropName={'checked'}
                        extra={t('User study open lock hint')}
                    >
                        <Switch/>
                    </Form.Item>

                    <Form.Item
                        label={t('Email')}
                        name={'email'}
                    >
                        <Input/>
                    </Form.Item>

                    <Form.Item
                        label={t('Phone')}
                        name="phone"
                        dependencies={['email', 'mfaEnabled']}
                        rules={[({getFieldValue}) => ({
                            // Yalnız kodun gideceği başka kanal yoksa zorunlu: 2FA açık ve e-posta boş.
                            validator: (_, value) =>
                                systemMfaEnabled && getFieldValue('mfaEnabled') && !getFieldValue('email') && !value?.phone
                                    ? Promise.reject(new Error(t('Requires a phone number or an e-mail address')))
                                    : Promise.resolve()
                        })]}
                    >
                        <PhoneInput />
                    </Form.Item>

                    <Form.Item
                        label={t('Name')}
                        name={'name'}
                        rules={[{required: true}]}
                    >
                        <Input/>
                    </Form.Item>

                    <Form.Item
                        label={t('Visible Name')}
                        name={'visibleName'}
                        rules={[{required: true}]}
                    >
                        <Input/>
                    </Form.Item>

                    <Form.Item
                        label={t('Password')}
                        name={'password'}
                        rules={[{required: userId ? false : true}]}
                    >
                        <Input
                            addonAfter={
                                <Button
                                    type={'link'}
                                    size={'small'}
                                    style={{height: 'auto', padding: 0}}
                                    onClick={() => formRef.current.setFieldsValue({password: generatePassword()})}
                                >
                                    {t('Generate Password')}
                                </Button>
                            }
                        />
                    </Form.Item>

                    <InstitutionSelect
                        formItem={{
                            name: 'institutionIdList',
                            label: t('Institutions')
                        }}
                        hide={true}
                    />

                    <div style={{padding: 8, border: '1px solid #e0e0e0'}}>
                        <Form.Item
                            label={t('Role')}
                            name={'roleId'}
                            rules={[{required: true}]}
                        >
                            <RoleSelectBox
                                restrictToOwnPermissions
                                keepPermissionIds={targetPermissions}
                                onRoleChange={(role) => {
                                    setDisabledPermissions(role.permissionIdList)
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            label={t('Other Permissions')}
                            name={'permissionIdList'}
                        >
                            <PermissionGroup
                                disabledPermission={disabledPermissions}
                                selectedPermissions={selectedPermissions}
                                onChangeSelectedPermissions={(permissions) => setSelectedPermissions(permissions)}
                            />
                        </Form.Item>
                    </div>

                    <Tabs defaultActiveKey="doctor-tab">
                        <TabPane tab={t('Doctor Information')} key="doctor-tab">
                            <Form.Item
                                label={t('TitleUnv')}
                                name={'prefix'}
                            >
                                <Input/>
                            </Form.Item>
                            <Form.Item
                                label={t('Name')}
                                name={'personName'}
                            >
                                <Input/>
                            </Form.Item>
                            <Form.Item
                                label={t('Middle Name')}
                                name={'personName2'}
                            >
                                <Input/>
                            </Form.Item>
                            <Form.Item
                                label={t('Lastname')}
                                name={'personLastname'}
                            >
                                <Input/>
                            </Form.Item>
                            <Form.Item
                                label={t('T.C.')}
                                name={'tcNo'}
                            >
                                <Input/>
                            </Form.Item>
                            <Form.Item
                                label={t('Diploma Number')}
                                name={'diplomaNumber'}
                            >
                                <Input/>
                            </Form.Item>
                            <Form.Item
                                label={t('Registration Code')}
                                name={'registrationCode'}
                            >
                                <Input/>
                            </Form.Item>
                            <Form.Item
                                label={t('Doctor Sign')}
                                name={'doctorSignPicture'}
                            >
                                <Flex flexDirection={'row'} alignItems={'center'}>
                                    {
                                        signImage
                                            ? (<img width={120} height={120} id={'doctor-sign-image'} src={signImage.url}/>)
                                            : (
                                                <Flex alignItems={'center'} style={{border: '1px solid black', height: 60, width: 120, textAlign: 'center'}}>
                                                    {t('Doctor Signature Image')}
                                                </Flex>
                                            )
                                    }
                                    <Button onClick={selectImage} style={{marginLeft: 8}}>{t('Select')}</Button>
                                </Flex>
                            </Form.Item>
                        </TabPane>
                    </Tabs>

                    <Form.Item>
                        <Button htmlType={'submit'}>{t('Save')}</Button>
                    </Form.Item>
                </Form>
        </>
    )
}
